// Cars / Fleet — backed by /admin/cars.

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  COUNTRIES,
  CONFIGURED_CURRENCIES,
  currencyForCountry,
  type ConfiguredCurrency,
} from '@drivecars/shared';
import {
  DataTable,
  type DataTableColumn,
  type RowKey,
} from '@drivecars/admin-ui';
import { CatalogPicker } from '@/components/CatalogPicker';
import { SearchSelect } from '@/components/forms/SearchSelect';
import { LocationPicker } from '@/components/forms/LocationPicker';
import {
  RelationshipPicker,
  type RelationshipOption,
} from '@/components/forms/RelationshipPicker';
import { PageHeader, ToolBtn, ToolChip, ToolSearch, Toolbar } from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  approveCar,
  archiveCar,
  bulkCarAction,
  createCar,
  downloadCarsCsv,
  featureCar,
  fetchAdminCars,
  fetchAdminLocations,
  fetchAdminVendors,
  rejectCar,
  unfeatureCar,
  type AdminCarRow,
  type AdminCarsListParams,
  type AdminCarsTestCrewFilter,
  type CarBulkAction,
} from '@/lib/admin-api';
import { ApiError } from '@/lib/api';
import { usePager } from '@/lib/use-pager';

type FilterKey = NonNullable<AdminCarsListParams['status']>;

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'published', label: 'Published' },
  { key: 'pending', label: 'Pending' },
  { key: 'draft', label: 'Draft' },
  { key: 'archived', label: 'Archived' },
];

// Test-mode filter: vendor id 99000 ("TEST QA Crew") clones a live
// supplier × 5 cities × airports = ~200 rows that pollute moderation.
// Defaults to hidden so the real fleet stays manageable; flip to "Test
// only" when QA'ing the clone or "All" to see both.
const TEST_CREW_FILTERS: Array<{ key: AdminCarsTestCrewFilter; label: string }> = [
  { key: 'exclude', label: 'Hide test cars' },
  { key: 'only', label: 'Test only' },
  { key: 'all', label: 'Show all' },
];

// Format the car's price in its NATIVE pricing currency (whatever the host
// listed it in — MAD for Morocco, AED for UAE, EUR for euro-area listings,
// etc.). The /admin/cars endpoint returns the raw `pricing_price` paired
// with `pricingCurrency`; if we hard-coded EUR the table would display
// "€350" for a row that actually means "MAD 350" — wildly misleading.
const fmtMoney = (v: number, currency: string) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(v);

interface ActionCtx {
  busy: number | null;
  onApprove: (r: AdminCarRow) => void;
  onReject: (r: AdminCarRow) => void;
  onArchive: (r: AdminCarRow) => void;
  onToggleFeature: (r: AdminCarRow) => void;
}

const buildColumns = (ctx: ActionCtx): DataTableColumn<AdminCarRow>[] => [
  {
    key: 'name',
    h: 'Car',
    cell: (r) => (
      <div className="flex items-center gap-3">
        <span
          className="h-9 w-12 shrink-0 overflow-hidden rounded-md bg-[color:var(--bo-bg)] bg-cover bg-center"
          style={r.imageUrl ? { backgroundImage: `url(${r.imageThumb ?? r.imageUrl})` } : undefined}
          aria-hidden
        />
        <div className="min-w-0">
          <div className="truncate font-medium">{r.title ?? r.slug}</div>
          <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
            {r.slug}
            {r.isFeatured ? ' · featured' : ''}
            {r.listingTitle && r.listingTitle !== r.title
              ? ` · vendor: ${r.listingTitle}`
              : ''}
          </div>
        </div>
      </div>
    ),
  },
  {
    key: 'vendor',
    h: 'Vendor',
    cell: (r) => r.vendorName ?? '—',
  },
  {
    key: 'location',
    h: 'Location',
    cell: (r) => r.locationTitle ?? r.locationSlug ?? '—',
  },
  {
    key: 'price',
    h: 'Price',
    right: true,
    cell: (r) => (
      <span className="tabular-nums">
        {fmtMoney(r.price, r.pricingCurrency)}
        <span className="text-[10px] text-[color:var(--bo-mute)]">
          {' '}
          / {r.priceUnit ?? 'day'}
        </span>
      </span>
    ),
  },
  {
    key: 'reviews',
    h: 'Reviews',
    right: true,
    cell: (r) => (
      <span className="tabular-nums text-[color:var(--bo-mute)]">{r.reviewCount ?? 0}</span>
    ),
  },
  {
    key: 'rating',
    h: 'Rating',
    right: true,
    cell: (r) =>
      r.reviewScore == null ? (
        <span className="text-[color:var(--bo-mute)]">—</span>
      ) : (
        <span className="tabular-nums">{r.reviewScore.toFixed(2)}</span>
      ),
  },
  {
    key: 'status',
    h: 'Status',
    cell: (r) => <StatusPill status={r.status} />,
  },
  {
    key: 'actions',
    h: 'Actions',
    cell: (r) => {
      const busy = ctx.busy === r.id;
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          {r.status === 'pending' || r.status === 'draft' ? (
            <RowAction kind="primary" busy={busy} onClick={() => ctx.onApprove(r)}>
              Approve
            </RowAction>
          ) : null}
          {r.status === 'pending' ? (
            <RowAction kind="danger" busy={busy} onClick={() => ctx.onReject(r)}>
              Reject
            </RowAction>
          ) : null}
          {r.status === 'published' ? (
            <RowAction busy={busy} onClick={() => ctx.onToggleFeature(r)}>
              {r.isFeatured ? 'Unfeature' : 'Feature'}
            </RowAction>
          ) : null}
          {r.status !== 'archived' ? (
            <RowAction busy={busy} onClick={() => ctx.onArchive(r)}>
              Archive
            </RowAction>
          ) : null}
        </div>
      );
    },
  },
];

export function Cars() {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [testCrew, setTestCrew] = useState<AdminCarsTestCrewFilter>('exclude');
  const [q, setQ] = useState('');
  // Country is uppercase ISO2 for display; the API lowercases it to match
  // cars.country_code. Location narrows to one pickup location (FK).
  const [country, setCountry] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [locationOption, setLocationOption] = useState<RelationshipOption | null>(null);
  const { page, setPage, limit, setLimit } = usePager();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createdSlug, setCreatedSlug] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<RowKey>>(new Set());

  // Any filter change lands back on page 1 — page N of the old result set
  // is meaningless against the new one.
  useEffect(() => {
    setPage(1);
  }, [filter, q, testCrew, country, locationId, setPage]);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadCarsCsv({
        status: filter,
        q: q.trim() || undefined,
        countryCode: country ?? undefined,
        locationId: locationId ?? undefined,
        testCrew,
      });
    } catch (err) {
      setExportError((err as Error).message);
    } finally {
      setExporting(false);
    }
  };
  const [bulkResult, setBulkResult] = useState<{
    succeeded: number;
    failed: number;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'cars', { filter, q, testCrew, country, locationId, page }],
    queryFn: ({ signal }) =>
      fetchAdminCars(
        {
          status: filter,
          q: q.trim() || undefined,
          countryCode: country ?? undefined,
          locationId: locationId ?? undefined,
          testCrew,
          page,
          limit,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'cars'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
  };

  const approveMut = useMutation({ mutationFn: approveCar, onSuccess: invalidate });
  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => rejectCar(id, reason),
    onSuccess: invalidate,
  });
  const archiveMut = useMutation({ mutationFn: archiveCar, onSuccess: invalidate });
  const featureMut = useMutation({ mutationFn: featureCar, onSuccess: invalidate });
  const unfeatureMut = useMutation({ mutationFn: unfeatureCar, onSuccess: invalidate });
  const bulkMut = useMutation({
    mutationFn: ({
      ids,
      action,
      reason,
    }: {
      ids: number[];
      action: CarBulkAction;
      reason?: string;
    }) => bulkCarAction(ids, action, reason),
    onSuccess: (res) => {
      setBulkResult({ succeeded: res.succeeded.length, failed: res.failed.length });
      setSelectedIds(new Set());
      invalidate();
    },
  });

  const runBulk = (action: CarBulkAction) => {
    const ids = Array.from(selectedIds, Number).filter(Number.isFinite);
    if (ids.length === 0) return;
    const verb =
      action === 'approve'
        ? 'Approve'
        : action === 'reject'
          ? 'Reject'
          : action === 'archive'
            ? 'Archive'
            : action === 'feature'
              ? 'Feature'
              : 'Unfeature';
    if (!window.confirm(`${verb} ${ids.length} listing${ids.length === 1 ? '' : 's'}?`)) {
      return;
    }
    let reason: string | undefined;
    if (action === 'reject') {
      reason = window.prompt('Reason for rejection (optional):') ?? undefined;
    }
    bulkMut.mutate({ ids, action, reason });
  };

  const busyId =
    (approveMut.isPending && (approveMut.variables as number)) ||
    (rejectMut.isPending && (rejectMut.variables as { id: number }).id) ||
    (archiveMut.isPending && (archiveMut.variables as number)) ||
    (featureMut.isPending && (featureMut.variables as number)) ||
    (unfeatureMut.isPending && (unfeatureMut.variables as number)) ||
    null;

  const cols = buildColumns({
    busy: busyId,
    onApprove: (r) => approveMut.mutate(r.id),
    onReject: (r) => {
      const reason = window.prompt('Rejection reason (optional):') ?? undefined;
      rejectMut.mutate({ id: r.id, reason });
    },
    onArchive: (r) => archiveMut.mutate(r.id),
    onToggleFeature: (r) =>
      r.isFeatured ? unfeatureMut.mutate(r.id) : featureMut.mutate(r.id),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const mutationError =
    approveMut.error ||
    rejectMut.error ||
    archiveMut.error ||
    featureMut.error ||
    unfeatureMut.error;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Operate"
        title="Cars · Fleet"
        sub={`${total.toLocaleString()} listings${filter === 'all' ? '' : ` · ${filter}`}${testCrew === 'only' ? ' · test crew only' : testCrew === 'all' ? ' · incl. test crew' : ''}.`}
        actions={
          <>
            <ToolBtn icon="download" onClick={() => void handleExport()}>
              {exporting ? 'Exporting…' : 'Export'}
            </ToolBtn>
            <ToolBtn icon="plus" primary onClick={() => setCreateOpen(true)}>
              New listing
            </ToolBtn>
          </>
        }
      />
      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn't load cars: {(error as Error).message}
        </div>
      ) : null}
      {mutationError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Mutation failed: {(mutationError as Error).message}
        </div>
      ) : null}
      {exportError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Export failed: {exportError}
        </div>
      ) : null}
      {createdSlug ? (
        <div
          role="status"
          className="flex items-center justify-between rounded-lg border border-[#a7f3d0] bg-[#ecfdf5] px-3 py-2 text-[12.5px] text-[#065f46]"
        >
          <span>
            Listing <strong>{createdSlug}</strong> created as a draft. Photos and base copy come
            from its catalog Car — open the listing to review details and publish.
          </span>
          <button
            type="button"
            onClick={() => setCreatedSlug(null)}
            className="text-[11px] underline-offset-2 hover:underline"
          >
            dismiss
          </button>
        </div>
      ) : null}
      <Toolbar>
        <ToolSearch value={q} onChange={setQ} placeholder="Search title, slug, vendor email…" />
        {FILTERS.map((f) => (
          <ToolChip key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label}
          </ToolChip>
        ))}
        <span aria-hidden className="mx-1 h-5 w-px self-center bg-[#e5e7eb]" />
        {TEST_CREW_FILTERS.map((f) => (
          <ToolChip key={f.key} active={testCrew === f.key} onClick={() => setTestCrew(f.key)}>
            {f.label}
          </ToolChip>
        ))}
      </Toolbar>
      <div className="grid max-w-2xl grid-cols-1 gap-2 sm:grid-cols-2">
        <SearchSelect
          label="Country"
          value={country}
          onChange={(v) => {
            setCountry(v);
            // A picked location almost never survives a country switch —
            // clear it rather than leave a contradictory AND filter.
            setLocationId(null);
            setLocationOption(null);
          }}
          options={COUNTRIES.map((c) => ({
            value: c.code,
            label: c.name,
            sub: c.code.toUpperCase(),
          }))}
          placeholder="All countries — type to filter…"
        />
        <RelationshipPicker
          label="Location"
          value={locationId}
          valueLabel={locationOption?.label}
          valueSub={locationOption?.sub ?? null}
          onChange={(id, opt) => {
            setLocationId(id);
            setLocationOption(opt ?? null);
          }}
          searchFn={async (search, signal) => {
            const res = await fetchAdminLocations(
              { q: search, countryCode: country ?? undefined, limit: 20 },
              signal,
            );
            return res.rows.map((r) => ({
              id: r.id,
              label: r.name ?? r.slug,
              sub: `${r.type}${r.countryCode ? ` · ${r.countryCode}` : ''}`,
            }));
          }}
          placeholder="All locations — search city / airport / station…"
        />
      </div>
      {bulkResult ? (
        <div
          role="status"
          className={`flex items-center justify-between rounded-lg border px-3 py-2 text-[12.5px] ${
            bulkResult.failed === 0
              ? 'border-[#a7f3d0] bg-[#ecfdf5] text-[#065f46]'
              : 'border-[#fde68a] bg-[#fef9c3] text-[#854d0e]'
          }`}
        >
          <span>
            Bulk action complete: {bulkResult.succeeded} succeeded
            {bulkResult.failed > 0 ? `, ${bulkResult.failed} failed` : ''}.
          </span>
          <button
            type="button"
            onClick={() => setBulkResult(null)}
            className="text-[11px] underline-offset-2 hover:underline"
          >
            dismiss
          </button>
        </div>
      ) : null}
      {selectedIds.size > 0 ? (
        <div className="sticky top-[60px] z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[color:var(--bo-line)] bg-white px-3 py-2 shadow-sm">
          <div className="text-[12.5px]">
            <strong>{selectedIds.size}</strong> selected
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="ms-3 text-[11.5px] text-[color:var(--bo-mute)] hover:underline"
            >
              clear
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <RowAction
              kind="primary"
              busy={bulkMut.isPending}
              onClick={() => runBulk('approve')}
            >
              Approve selected
            </RowAction>
            <RowAction busy={bulkMut.isPending} onClick={() => runBulk('reject')}>
              Reject selected
            </RowAction>
            <RowAction busy={bulkMut.isPending} onClick={() => runBulk('feature')}>
              Feature
            </RowAction>
            <RowAction busy={bulkMut.isPending} onClick={() => runBulk('unfeature')}>
              Unfeature
            </RowAction>
            <RowAction
              kind="danger"
              busy={bulkMut.isPending}
              onClick={() => runBulk('archive')}
            >
              Archive
            </RowAction>
          </div>
        </div>
      ) : null}
      <DataTable
        columns={cols}
        rows={rows}
        onRowClick={(r) => void navigate({ to: `/cars/${r.id}` as never })}
        emptyLabel={isLoading ? 'Loading…' : 'No matching cars.'}
        pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
        selection={{
          selectedIds,
          onToggle: (id) => {
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            });
          },
          onToggleAll: (ids) => {
            setSelectedIds((prev) => {
              const allHere = ids.every((id) => prev.has(id));
              const next = new Set(prev);
              if (allHere) {
                for (const id of ids) next.delete(id);
              } else {
                for (const id of ids) next.add(id);
              }
              return next;
            });
          },
        }}
      />
      {createOpen ? (
        <NewListingModal
          onClose={() => setCreateOpen(false)}
          onCreated={(slug) => {
            setCreateOpen(false);
            setCreatedSlug(slug);
            void queryClient.invalidateQueries({ queryKey: ['admin', 'cars'] });
          }}
        />
      ) : null}
    </div>
  );
}

function NewListingModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (slug: string) => void;
}) {
  // carId + title come from the catalog picker; the operator can't type a
  // free-form title here — it's derived from the chosen catalog entry and
  // refined later on the detail page.
  const [carId, setCarId] = useState<number | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [vendorId, setVendorId] = useState('');
  const [country, setCountry] = useState('');
  const [locationId, setLocationId] = useState<number | null>(null);
  const [locationOption, setLocationOption] = useState<{ id: number; label: string; sub?: string | null } | null>(null);
  const [price, setPrice] = useState('');
  const [minDays, setMinDays] = useState('');
  const [unit, setUnit] = useState<'day' | 'hour'>('day');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  // Only consulted when the chosen country has no mapped currency; a mapped
  // country's currency is derived (read-only) from `country`.
  const [manualCurrency, setManualCurrency] = useState<ConfiguredCurrency>('EUR');

  const autoCurrency = country ? currencyForCountry(country) : null;
  const effectiveCurrency: ConfiguredCurrency = autoCurrency ?? manualCurrency;

  // Fetched once and filtered client-side by SearchSelect. The limit is the
  // cap on what is searchable at all — anything beyond it is invisible to the
  // picker, so keep it comfortably above the vendor count and tell the operator
  // when it truncates (below) instead of silently hiding vendors.
  const VENDOR_PICKER_LIMIT = 500;
  const vendorsQ = useQuery({
    queryKey: ['admin', 'vendors', { forPicker: true, limit: VENDOR_PICKER_LIMIT }],
    queryFn: ({ signal }) => fetchAdminVendors({ limit: VENDOR_PICKER_LIMIT }, signal),
  });
  const vendorRows = vendorsQ.data?.rows ?? [];
  const vendorOptions = vendorRows.map((v) => ({
    value: String(v.id),
    label: v.displayName,
    // Email disambiguates vendors sharing a display name — and is searchable,
    // since SearchSelect matches on both label and value... but not sub, so the
    // email is a visual tiebreaker only.
    sub: v.email,
  }));
  const vendorsTruncated = (vendorsQ.data?.total ?? 0) > vendorRows.length;
  const mut = useMutation({
    mutationFn: () =>
      createCar({
        carId: carId!,
        countryCode: country,
        currency: effectiveCurrency,
        unit,
        title: title ?? undefined,
        vendorId: Number(vendorId),
        locationId: locationId ?? undefined,
        price: Number(price),
        // Clamp to whole days >= 1 — the modal submit path doesn't run the
        // input's native min/step validation.
        minDays:
          minDays.trim() !== '' && Number.isFinite(Number(minDays))
            ? Math.max(1, Math.round(Number(minDays)))
            : undefined,
        status,
      }),
    onSuccess: (res) => onCreated(res.slug),
  });

  const errorMessage = (() => {
    const err = mut.error;
    if (!err) return null;
    if (err instanceof ApiError) {
      const code =
        err.body && typeof err.body === 'object' && 'error' in err.body
          ? String((err.body as { error?: unknown }).error)
          : null;
      if (code === 'slug_taken') return 'That URL slug is already in use — try a different one.';
      if (code === 'catalog_entry_not_found') return 'Pick a vehicle from the catalog.';
      if (code === 'vendor_not_found') return 'Pick a valid vendor.';
      if (code === 'location_not_found') return 'Pick a valid location.';
    }
    return 'Could not create the listing. Please try again.';
  })();

  const canSubmit =
    carId != null &&
    carId > 0 &&
    vendorId !== '' &&
    country !== '' &&
    price.trim() !== '' &&
    Number(price) > 0 &&
    !mut.isPending;

  // Name the first unmet requirement. A disabled submit with no explanation is
  // indistinguishable from a broken form — operators hit exactly that when a
  // multi-trim catalog model left `carId` null while every visible field looked
  // filled in. Ordered to match the form top-to-bottom.
  const blockingReason = (() => {
    if (mut.isPending) return null;
    if (carId == null || carId <= 0) return 'Choose a vehicle from the catalog above (make, model, and trim).';
    if (vendorId === '') return 'Select a vendor.';
    if (country === '') return 'Select a country.';
    if (price.trim() === '' || !(Number(price) > 0)) return 'Enter a price greater than 0.';
    return null;
  })();
  const field =
    'h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-mute)] disabled:opacity-50';
  const lbl = 'text-[12px] font-medium text-[color:var(--bo-ink)]';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="New listing"
      onClick={onClose}
    >
      <form
        className="max-h-[90vh] w-full max-w-lg space-y-3 overflow-y-auto rounded-xl border border-[color:var(--bo-line)] bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) mut.mutate();
        }}
      >
        <div>
          <h2 className="text-[15px] font-semibold text-[color:var(--bo-ink)]">New listing</h2>
          <p className="mt-0.5 text-[12px] text-[color:var(--bo-mute)]">
            Creates a draft listing of the picked catalog Car. Photos and base copy come from the
            catalog entry; add listing-specific details (description override, address, deposit)
            on the listing page afterwards.
          </p>
        </div>

        {errorMessage ? (
          <div className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
            {errorMessage}
          </div>
        ) : null}

        <CatalogPicker
          disabled={mut.isPending}
          onEntryResolved={({ id, title: resolved }) => {
            setCarId(id);
            setTitle(resolved);
          }}
        />

        <SearchSelect
          label="Vendor"
          required
          disabled={mut.isPending}
          value={vendorId || null}
          onChange={(v) => setVendorId(v ?? '')}
          options={vendorOptions}
          placeholder={vendorsQ.isLoading ? 'Loading vendors…' : 'Type to search vendors…'}
          emptyLabel={vendorsQ.isLoading ? 'Loading…' : 'No vendors match.'}
          hint={
            vendorsTruncated
              ? // Filtering is client-side over the rows already fetched, so a
                // vendor past the cap cannot be typed into view — say that
                // plainly rather than implying a narrower query would find it.
                `Only the first ${vendorRows.length} of ${vendorsQ.data?.total} vendors are searchable here — set the vendor from the listing page if yours is missing.`
              : undefined
          }
        />

        <div className="grid grid-cols-2 gap-2">
          <SearchSelect
            label="Country"
            required
            value={country || null}
            onChange={(v) => setCountry(v ?? '')}
            options={COUNTRIES.map((c) => ({ value: c.code, label: c.name, sub: c.code.toUpperCase() }))}
            placeholder="Search country…"
          />
          <label className="block space-y-1">
            <span className={lbl}>Currency</span>
            {autoCurrency ? (
              <input
                readOnly
                aria-label="Currency (auto from country)"
                value={autoCurrency}
                className={`${field} bg-[color:var(--bo-bg)] text-[color:var(--bo-mute)]`}
              />
            ) : (
              <select
                aria-label="Currency"
                value={manualCurrency}
                disabled={country === ''}
                onChange={(e) => setManualCurrency(e.target.value as ConfiguredCurrency)}
                className={field}
              >
                {CONFIGURED_CURRENCIES.map((cur) => (
                  <option key={cur} value={cur}>
                    {cur}
                  </option>
                ))}
              </select>
            )}
          </label>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span className={lbl}>Price ({effectiveCurrency}) *</span>
            <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className={field} />
          </label>
          <label className="block space-y-1">
            <span className={lbl}>Per</span>
            <select value={unit} onChange={(e) => setUnit(e.target.value as 'day' | 'hour')} className={field}>
              <option value="day">Day</option>
              <option value="hour">Hour</option>
            </select>
          </label>
        </div>

        <label className="block space-y-1">
          <span className={lbl}>Minimum rental (days)</span>
          <input
            type="number"
            min="1"
            step="1"
            value={minDays}
            onChange={(e) => setMinDays(e.target.value)}
            placeholder="Optional — shorter bookings are rejected"
            className={field}
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <LocationPicker
            label="Location (optional)"
            value={locationId}
            valueLabel={locationOption?.label}
            valueSub={locationOption?.sub ?? null}
            onChange={(id, opt) => {
              setLocationId(id);
              setLocationOption(opt ?? null);
            }}
            searchFn={async (q, signal) => {
              const res = await fetchAdminLocations({ q, limit: 20 }, signal);
              return res.rows.map((r) => ({
                id: r.id,
                label: r.name ?? r.slug,
                sub: `${r.type}${r.countryCode ? ` · ${r.countryCode}` : ''}`,
              }));
            }}
            placeholder="Search city / airport / station…"
          />
          <label className="block space-y-1">
            <span className={lbl}>Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as 'draft' | 'published')} className={field}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </label>
        </div>

        {blockingReason ? (
          <p className="pt-1 text-right text-[11.5px] text-[color:var(--bo-mute)]">{blockingReason}</p>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] font-medium text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="h-9 rounded-lg bg-[color:var(--bo-ink)] px-3 text-[13px] font-medium text-white hover:bg-black disabled:opacity-50"
          >
            {mut.isPending ? 'Creating…' : 'Create listing'}
          </button>
        </div>
      </form>
    </div>
  );
}
